import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { PageHeader } from "../UsmLayout";
import Can from "@/components/usm/Can";
import { toast } from "sonner";
import { Plus, Eye, Truck, RefreshCw, ScanLine, X, Trash2 } from "lucide-react";

const STATUS = {
  draft:      { l: "Taslak",       cls: "text-stone-700" },
  in_transit: { l: "Yolda",        cls: "text-amber-700" },
  completed:  { l: "Tamamlandı",   cls: "text-emerald-700" },
  cancelled:  { l: "İptal",        cls: "text-red-600" },
};

export default function TransfersList() {
  const { isSuperAdmin } = useUsmAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 50;
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", branch_id: "", date_from: "", date_to: "" });
  const [branches, setBranches] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filter.status) qs.set("status", filter.status);
      if (filter.branch_id) qs.set("branch_id", filter.branch_id);
      if (filter.date_from) qs.set("date_from", filter.date_from);
      if (filter.date_to) qs.set("date_to", filter.date_to);
      qs.set("page", p); qs.set("limit", limit);
      const r = await usmApi.get(`/transfers?${qs.toString()}`);
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    } catch (e) { toast.error("Transferler yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { usmApi.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { setPage(1); load(1); /* eslint-disable-next-line */ }, [filter.status, filter.branch_id, filter.date_from, filter.date_to]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div data-testid="usm-transfers">
      <PageHeader title="Stok Transferleri" breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Transferler" }]}
        actions={<Can perm="transfers.create">
          <button data-testid="transfer-new-btn" onClick={() => setCreating(true)}
            className="bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] px-5 py-3 inline-flex items-center gap-2">
            <Plus size={14} /> Yeni Transfer
          </button>
        </Can>} />

      <div className="bg-white border border-[#E7E5E4] mb-4 p-3 flex flex-wrap gap-3">
        <select data-testid="transfer-status-filter" value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))} className="px-3 py-2 border border-[#E7E5E4] text-sm">
          <option value="">Tüm durumlar</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
        </select>
        {isSuperAdmin && (
          <select data-testid="transfer-branch-filter" value={filter.branch_id} onChange={(e) => setFilter((f) => ({ ...f, branch_id: e.target.value }))} className="px-3 py-2 border border-[#E7E5E4] text-sm">
            <option value="">Tüm şubeler</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <input type="date" value={filter.date_from} onChange={(e) => setFilter((f) => ({ ...f, date_from: e.target.value }))} className="px-3 py-2 border border-[#E7E5E4] text-sm" />
        <input type="date" value={filter.date_to} onChange={(e) => setFilter((f) => ({ ...f, date_to: e.target.value }))} className="px-3 py-2 border border-[#E7E5E4] text-sm" />
        <button onClick={() => load()} className="px-3 py-2 border border-[#E7E5E4] hover:bg-[#FAFAF9]"><RefreshCw size={14} /></button>
      </div>

      <div className="bg-white border border-[#E7E5E4] overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-[#FAFAF9] text-[11px] uppercase tracking-[0.15em] text-[#71717A]">
            <tr>
              <th className="text-left px-3 py-3">Transfer No</th>
              <th className="text-left px-3 py-3">Tarih</th>
              <th className="text-left px-3 py-3">Kaynak</th>
              <th className="text-left px-3 py-3">Hedef</th>
              <th className="text-right px-3 py-3">Ürün</th>
              <th className="text-right px-3 py-3">Toplam Adet</th>
              <th className="text-left px-3 py-3">Durum</th>
              <th className="text-right px-3 py-3 w-20">Detay</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="px-4 py-6 text-center text-[#71717A]">Yükleniyor...</td></tr>
              : items.length === 0 ? <tr><td colSpan={8} className="px-4 py-10 text-center text-[#71717A]">Henüz transfer yok.</td></tr>
              : items.map((t) => {
                const st = STATUS[t.status] || { l: t.status, cls: "" };
                return (
                  <tr key={t.id} data-testid={`transfer-row-${t.transfer_no}`} className="border-t border-[#F5F5F4]">
                    <td className="px-3 py-3 font-mono text-[12px] font-semibold">{t.transfer_no}</td>
                    <td className="px-3 py-3 text-[#52525B]">{new Date(t.created_at).toLocaleString("tr-TR")}</td>
                    <td className="px-3 py-3">{t.source_branch?.name || "—"}</td>
                    <td className="px-3 py-3 inline-flex items-center gap-1.5"><Truck size={12} className="text-[#71717A]" /> {t.destination_branch?.name || "—"}</td>
                    <td className="px-3 py-3 text-right font-finance">{t.items_count}</td>
                    <td className="px-3 py-3 text-right font-finance font-semibold">{t.total_quantity ?? "—"}</td>
                    <td className={"px-3 py-3 text-[11px] uppercase tracking-[0.15em] " + st.cls}>● {st.l}</td>
                    <td className="px-3 py-3 text-right">
                      <Link data-testid={`transfer-detail-${t.transfer_no}`} to={`/usm/transfers/${t.id}`} className="p-2 inline-flex hover:bg-[#FAFAF9]"><Eye size={14} /></Link>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-[#71717A]">
          <span>Toplam {total} transfer</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => { const np = page - 1; setPage(np); load(np); }} className="px-3 py-2 border border-[#E7E5E4] disabled:opacity-30">‹</button>
            <span>Sayfa {page}/{totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => { const np = page + 1; setPage(np); load(np); }} className="px-3 py-2 border border-[#E7E5E4] disabled:opacity-30">›</button>
          </div>
        </div>
      )}

      {creating && <TransferCreator branches={branches} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function TransferCreator({ branches, onClose, onSaved }) {
  const { employee, isSuperAdmin } = useUsmAuth();
  const [src, setSrc] = useState(isSuperAdmin ? "" : (employee?.branch_id || ""));
  const [dst, setDst] = useState("");
  const [note, setNote] = useState("");
  // Phase 51 — barcode-scan-driven workflow. `lines` is an ordered list of
  // scanned rows keyed by inventory_id. Scanning the same product twice bumps
  // its quantity instead of duplicating a row.
  const [lines, setLines] = useState([]); // [{ item, quantity }]
  const [code, setCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { url, name } | null
  const inputRef = useRef(null);

  // Reset the scanned list whenever the source branch changes — scanning
  // rows across branches would violate the API's per-line "row belongs to
  // source" check.
  useEffect(() => { setLines([]); setCode(""); }, [src]);

  // Keep focus on the scan box so USB scanners "just work" like Fiyat Sorgu.
  useEffect(() => { if (src && dst) inputRef.current?.focus(); }, [src, dst]);

  const totalUnits = useMemo(
    () => lines.reduce((a, l) => a + (parseInt(l.quantity, 10) || 0), 0),
    [lines],
  );

  const invalidLine = useMemo(() => {
    for (const l of lines) {
      const max = Math.max(0, parseInt(l.item?.quantity ?? 0, 10));
      const q = parseInt(l.quantity, 10);
      if (!Number.isFinite(q) || q < 1) return l.item?.id;
      if (q > max) return l.item?.id;
    }
    return null;
  }, [lines]);

  const valid = !!src && !!dst && src !== dst && lines.length > 0 && !invalidLine;

  const scanCode = async (raw) => {
    const needle = (raw ?? code).trim();
    if (!needle) return;
    if (!src) { toast.error("Önce kaynak şubeyi seçin"); return; }
    setScanning(true);
    try {
      const r = await usmApi.get(`/transfers/scan/${encodeURIComponent(needle)}?source_branch_id=${encodeURIComponent(src)}`);
      const item = r.data;
      // If already scanned → bump qty (respecting available stock)
      setLines((prev) => {
        const idx = prev.findIndex((l) => l.item.id === item.id);
        const maxStock = Math.max(0, parseInt(item.quantity ?? 0, 10));
        if (idx >= 0) {
          const nextQ = Math.min(maxStock, (parseInt(prev[idx].quantity, 10) || 0) + 1);
          if (nextQ === prev[idx].quantity) {
            toast.info(`${item.stock_code} — kaynak stok üst sınırına ulaşıldı (${maxStock})`);
            return prev;
          }
          const copy = prev.slice();
          copy[idx] = { ...copy[idx], quantity: nextQ };
          return copy;
        }
        if (maxStock <= 0) { toast.error(`${item.stock_code} kaynak şubede stokta değil`); return prev; }
        return [...prev, { item, quantity: 1 }];
      });
      setCode("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Ürün bulunamadı";
      toast.error(msg);
    } finally {
      setScanning(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const setQty = (iid, q) => setLines((prev) =>
    prev.map((l) => l.item.id === iid ? { ...l, quantity: Number.isFinite(q) ? q : 1 } : l)
  );
  const removeLine = (iid) => setLines((prev) => prev.filter((l) => l.item.id !== iid));

  const submit = async () => {
    setSaving(true);
    try {
      const payloadItems = lines.map((l) => ({
        inventory_id: l.item.id,
        quantity: parseInt(l.quantity, 10) || 1,
      }));
      const r = await usmApi.post("/transfers", {
        source_branch_id: src, destination_branch_id: dst, items: payloadItems, note,
      });
      toast.success(`Transfer ${r.data.transfer_no} oluşturuldu (${totalUnits} adet)`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Transfer başarısız"); }
    finally { setSaving(false); }
  };

  const onScanSubmit = (e) => { e.preventDefault(); scanCode(); };

  // Phase 51.1 — When the image preview is open we treat ESC as "close
  // preview only". The event is stopped so it can never propagate up to any
  // ancestor listener that would close the Transfer dialog itself.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setLightbox(null);
      }
    };
    // capture:true → we see the event before any bubble-phase listener that
    // might otherwise interpret ESC as "close the whole modal stack".
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [lightbox]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div data-testid="transfer-creator" className="bg-white w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <h2 className="font-heading text-xl">Yeni Stok Transferi</h2>
          <button onClick={onClose} className="text-[#71717A] text-lg">✕</button>
        </div>

        {/* Metadata row — source / destination / note */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Kaynak Şube *">
            <select data-testid="transfer-src" value={src} onChange={(e) => setSrc(e.target.value)}
              disabled={!isSuperAdmin} className={inputCls}>
              <option value="">Seçin...</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <Field label="Hedef Şube *">
            <select data-testid="transfer-dst" value={dst} onChange={(e) => setDst(e.target.value)} className={inputCls}>
              <option value="">Seçin...</option>
              {branches.filter((b) => b.id !== src).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <Field label="Transfer Notu"><input data-testid="transfer-note" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="Opsiyonel" /></Field>
        </div>

        {/* Barcode scan input — mirrors Fiyat Sorgu */}
        <div className="px-6 pb-4">
          <form onSubmit={onScanSubmit} className="border border-[#E7E5E4] bg-[#FAFAF9] p-3">
            <label className="text-[10.5px] uppercase tracking-[0.2em] text-[#71717A] block mb-2">
              Ürün Ekle — Barkod veya Stok Kodu
            </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <ScanLine size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C8A96A] pointer-events-none" />
                <input
                  ref={inputRef}
                  data-testid="transfer-scan-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={!src || !dst}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={src && dst ? "Barkodu okutun veya stok kodu yazın (UZG000001)" : "Önce kaynak ve hedef şubeyi seçin"}
                  className="w-full pl-11 pr-10 py-3 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-base font-mono tracking-wider disabled:bg-[#F5F5F4] disabled:text-[#A8A29E]"
                />
                {code && (
                  <button type="button" onClick={() => setCode("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white text-[#71717A]">
                    <X size={16} />
                  </button>
                )}
              </div>
              <button data-testid="transfer-scan-submit" type="submit" disabled={!code || scanning || !src || !dst}
                className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-5 py-3">
                {scanning ? "Aranıyor..." : "Ekle"}
              </button>
            </div>
            <p className="text-[10.5px] text-[#71717A] mt-1.5">
              ↳ Her okutma listeye ekler. Aynı ürünü tekrar okutmak adedi artırır.
            </p>
          </form>
        </div>

        {/* Scanned line items */}
        <div className="px-6 pb-2 flex items-center justify-between">
          <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#71717A]">
            Okutulan Ürünler ({lines.length} satır · {totalUnits} adet)
          </h3>
          {lines.length > 0 && (
            <button data-testid="transfer-clear-lines" onClick={() => setLines([])}
              className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] hover:text-red-600 hover:underline">
              Listeyi Temizle
            </button>
          )}
        </div>
        <div className="px-6 pb-4 flex-1 overflow-y-auto">
          {lines.length === 0 ? (
            <div className="text-center text-[#A8A29E] py-10 text-sm border border-dashed border-[#E7E5E4]">
              {!src ? "Kaynak şubeyi seçin." : !dst ? "Hedef şubeyi seçin." : "Ürün okutulmadı."}
            </div>
          ) : (
            <ul className="space-y-1.5">
              {lines.map(({ item, quantity }) => {
                const max = Math.max(0, parseInt(item.quantity ?? 0, 10));
                const q = parseInt(quantity, 10);
                const lineInvalid = !Number.isFinite(q) || q < 1 || q > max;
                const photo = item.photo_urls?.[0];
                return (
                  <li key={item.id} data-testid={`transfer-line-${item.stock_code}`}
                    className={"flex items-center gap-3 px-3 py-2 border " + (lineInvalid ? "border-red-300 bg-red-50" : "border-[#C8A96A] bg-[#FBF5E8]")}>
                    {/* Photo — click to open lightbox (mirrors Envanter) */}
                    {photo ? (
                      <button type="button" onClick={() => setLightbox({ url: photo, name: item.name })}
                        data-testid={`transfer-line-photo-${item.stock_code}`}
                        className="w-12 h-12 bg-white border border-[#F5F5F4] flex-shrink-0 overflow-hidden">
                        <img src={photo} alt="" className="w-full h-full object-contain" />
                      </button>
                    ) : (
                      <div className="w-12 h-12 bg-white border border-[#F5F5F4] flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-3">
                        <span className="font-mono text-[11.5px] text-[#0A0A0A] font-semibold">{item.stock_code}</span>
                        {item.barcode && item.barcode !== item.stock_code && (
                          <span className="font-mono text-[10px] text-[#71717A]">{item.barcode}</span>
                        )}
                      </div>
                      <div className="text-sm font-medium truncate">{item.name}</div>
                      <div className="text-[11px] text-[#71717A]">
                        {item.material}{item.weight_grams != null && ` · ${item.weight_grams}g`}
                        <span className="ml-2 text-[#52525B]">Kaynak Stok: <span className="font-mono">{max}</span></span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <label className="text-[10px] uppercase tracking-[0.15em] text-[#71717A]">Adet</label>
                      <input
                        type="number"
                        min={1}
                        max={max}
                        value={quantity}
                        onChange={(e) => {
                          const raw = e.target.value === "" ? 1 : parseInt(e.target.value, 10);
                          setQty(item.id, Number.isFinite(raw) ? raw : 1);
                        }}
                        data-testid={`transfer-qty-${item.stock_code}`}
                        className={"w-20 px-2 py-1.5 border text-sm font-mono text-right " + (lineInvalid ? "border-red-400 bg-red-50 text-red-700" : "border-[#E7E5E4]")}
                      />
                      <button type="button" onClick={() => removeLine(item.id)}
                        data-testid={`transfer-remove-${item.stock_code}`}
                        className="p-2 text-[#71717A] hover:text-red-600 hover:bg-white">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E7E5E4] flex justify-between items-center gap-3">
          {invalidLine && (
            <span className="text-[11px] text-red-600">Geçersiz adet — mevcut stoktan fazla giremezsiniz.</span>
          )}
          <div className="flex gap-3 ml-auto">
            <button onClick={onClose} className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9]">İptal</button>
            <button data-testid="transfer-submit" onClick={submit} disabled={!valid || saving}
              className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">
              {saving ? "Gönderiliyor..." : `Gönder (${totalUnits} adet)`}
            </button>
          </div>
        </div>
      </div>

      {/* Image lightbox — child overlay of the Transfer dialog.
          Phase 51.1: every mouse event that lands here is fully swallowed
          so it can NEVER reach the parent modal's backdrop-close handler.
          Closing the preview leaves all scanned lines, quantities, and the
          transfer note untouched. */}
      {lightbox && (
        <div
          data-testid="transfer-lightbox"
          style={{ zIndex: 200 }}
          className="fixed inset-0 bg-black/85 flex items-center justify-center p-6"
          onMouseDown={(e) => { e.stopPropagation(); }}
          onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
        >
          <button
            data-testid="transfer-lightbox-close"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            aria-label="Önizlemeyi kapat"
            style={{ zIndex: 210 }}
            className="fixed top-4 left-4 bg-white text-[#0A0A0A] rounded-full w-11 h-11 flex items-center justify-center shadow-lg ring-2 ring-black/40 hover:bg-[#C8A96A] hover:text-white transition-colors"
          >
            <X size={22} strokeWidth={2.5} />
          </button>
          <img
            src={lightbox.url}
            alt={lightbox.name || ""}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] block mb-1.5">{label}</label>{children}</div>;
}
const inputCls = "w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm";
